/**
 * The doctor home (docs/ux/dr.md): "My reports" for a doctor or the
 * presenter acting as one, a Today filter, the read-only profile tab; for
 * an admin or QA the doctors with their counts, and any doctor's queue
 * through `?doctor=`. The phone is the doctor's first surface: cards, a
 * 44 px segmented control, one tap to the workspace.
 */

import type { SortingState } from '@tanstack/react-table';
import { useMemo } from 'react';
import { Link } from 'react-router';
import { say } from '@/app/announcer';
import { useOnline } from '@/app/online';
import { useSession } from '@/app/session';
import type { DoctorCounts, DoctorRow } from '@/app/types';
import { useIsPhone } from '@/app/viewport';
import { DaysCell } from '@/components/matter/DaysCell';
import { FlagMenu } from '@/components/matter/FlagMenu';
import { StatusChip } from '@/components/matter/StatusChip';
import { Legend, LegendItem } from '@/components/shell/Legend';
import { MatterCard, MatterCardList } from '@/components/shell/MatterCard';
import { PageHeader } from '@/components/shell/PageHeader';
import { QueueTable } from '@/components/shell/QueueTable';
import { QueueTabs } from '@/components/shell/QueueTabs';
import { EmptyState, ErrorPanel } from '@/components/shell/states';
import { useShellWidth } from '@/components/shell/context';
import { Button, buttonVariants } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { DueCell } from '@/features/pipeline/columns';
import { pluralise } from '@/lib/format';
import { InactiveMark, doctorColumns, doctorsColumns, openLabel, turnWords } from './columns';
import { BookingsTab } from './BookingsTab';
import { ProfileTab } from './ProfileTab';
import { doctorRowHref, todayRows, useDoctorCounts, useDoctorQueue, useDrFilters, type DrFilters } from './useDoctorQueue';

const TODAY_HINT = 'Today: waiting on you and overdue, due within 48 hours or flagged Urgent.';

/** "Dr Harriet Vale" becomes "Dr Vale" for the possessive sentences. */
function shortName(name: string): string {
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2 && /^(Dr|Professor|Associate|Mr|Ms)$/i.test(parts[0]!) ? `${parts[0]} ${parts[parts.length - 1]}` : name;
}

export function DoctorHomePage() {
  useShellWidth('wide');
  const { me, status } = useSession();
  const { filters, update } = useDrFilters();
  const requested = filters.doctor;
  const slug = requested ?? me?.doctorSlug ?? null;
  const mine = slug !== null && slug === me?.doctorSlug && !requested;
  // The persona decides the surface; until the session answers, neither the list nor a queue is right.
  if (status === 'loading' && requested === null) return <Skeleton className="mt-6 h-64 rounded-xl" aria-hidden="true" />;
  if (slug === null) return <DoctorsList />;
  return <DoctorQueue slug={slug} mine={mine} filters={filters} update={update} />;
}

function DoctorQueue({ slug, mine, filters, update }: { slug: string; mine: boolean; filters: DrFilters; update: (p: Partial<DrFilters>) => void }) {
  const queue = useDoctorQueue(slug);
  const phone = useIsPhone();
  const online = useOnline();
  const rows = queue.data?.rows ?? [];
  const today = useMemo(() => todayRows(rows), [rows]);
  const visible = filters.show === 'today' ? today : rows;
  const onBridge = queue.data?.doctor?.dictationMode === 'bridge';
  const columns = useMemo(() => doctorColumns(mine, onBridge), [mine, onBridge]);
  const sorting: SortingState = filters.sort ? [{ id: filters.sort, desc: filters.dir === 'desc' }] : [];
  const doctorName = queue.data?.doctor?.name ?? slug;
  const short = shortName(doctorName);

  const title = mine ? 'My reports' : queue.data?.doctor?.name ?? 'Doctor';
  const description = filters.tab === 'profile'
    ? (mine ? 'Your name, credential line and signature block as the release prints them.' : `${short}'s name, credential line and signature block as the release prints them.`)
    : filters.tab === 'bookings'
      ? (mine ? 'Your appointments for the coming week, in time order.' : `${short}'s appointments for the coming week, in time order.`)
      : (mine ? 'Reports waiting on you, in due-date order.' : `Reports waiting on ${short}, in due-date order.`);
  const tabs = [
    { value: 'reports', label: mine ? 'My reports' : 'Reports', ...(queue.data ? { count: rows.length } : {}) },
    { value: 'bookings', label: 'Upcoming bookings' },
    { value: 'profile', label: mine ? 'How I appear on the report' : 'How they appear on the report' },
  ];
  const header = (
    <PageHeader
      title={title}
      documentTitle={mine ? 'My reports' : title}
      eyebrow={mine ? undefined : <Link to="/dr" className="text-sm text-beam-signature underline-offset-2 hover:underline">Doctors</Link>}
      description={description}
      tabs={<QueueTabs tabs={tabs} value={filters.tab} onChange={(v) => { say(v === 'reports' ? `${tabs[0]!.label}, ${rows.length}` : tabs.find((t) => t.value === v)!.label); update({ tab: v as DrFilters['tab'] }); }} label="Doctor home tabs" />}
    />
  );

  if (queue.data === null) {
    return (
      <>
        {header}
        <EmptyState className="mt-6" title={`No doctor with the key ${slug}.`} action={<Link to="/dr" className={buttonVariants({ variant: 'outline' })}>Open the doctors list</Link>} />
      </>
    );
  }
  if (filters.tab === 'profile') {
    return (
      <div className="flex flex-col gap-6">
        {header}
        <ProfileTab slug={slug} mine={mine} />
      </div>
    );
  }
  if (filters.tab === 'bookings') {
    return (
      <div className="flex flex-col gap-3">
        {header}
        <BookingsTab slug={slug} mine={mine} from={filters.from} onMove={(from) => update({ from })} />
      </div>
    );
  }
  if (queue.isError) {
    return (
      <div className="flex flex-col gap-3">
        {header}
        <ErrorPanel message={mine ? 'Your reports could not be loaded.' : `${short}'s reports could not be loaded.`} onRetry={() => void queue.refetch()} offline={!online} />
      </div>
    );
  }

  const inactive = queue.data && queue.data.doctor && !queue.data.doctor.active;
  const showTabs = [
    { value: 'today', label: 'Today', ...(queue.data ? { count: today.length } : {}) },
    { value: 'all', label: mine ? 'All my reports' : 'All reports', ...(queue.data ? { count: rows.length } : {}) },
  ];
  const empty = filters.show === 'today' ? (
    <EmptyState title="Nothing is due today." description={mine ? 'Nothing waiting on you is overdue, due within 48 hours or urgent.' : `Nothing waiting on ${short} is overdue, due within 48 hours or urgent.`} action={<Button variant="outline" size={phone ? 'lg' : 'default'} onClick={() => update({ show: 'all' })}>{mine ? 'Show all my reports' : 'Show all reports'}</Button>} />
  ) : (
    <EmptyState title={mine ? 'Nothing is on your queue.' : `Nothing is on ${short}'s queue.`} description="Reports appear here when a case reaches the doctor at 6 Awaiting Doctor Draft. Completed reports are on the pipeline." action={mine ? undefined : <Link to="/pipeline" className={buttonVariants({ variant: 'outline' })}>Open the pipeline</Link>} />
  );

  return (
    <div className="flex flex-col gap-3">
      {header}
      {inactive ? <p className="text-sm text-beam-text-muted">This doctor is inactive; reports still on their queue are listed.</p> : null}
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-4">
        <QueueTabs tabs={showTabs} value={filters.show} onChange={(v) => { const next = v as DrFilters['show']; say(next === 'today' ? `Today, ${today.length}` : `${showTabs[1]!.label}, ${rows.length}`); update({ show: next }); }} label="Show" panelId="dr-queue-panel" />
        <p className="text-xs text-beam-text-muted">{TODAY_HINT}</p>
      </div>
      {queue.data && queue.data.today.pastReportMark > 0 ? (
        <p className="text-sm font-medium text-beam-error" data-slot="report-mark">{pluralise(queue.data.today.pastReportMark, 'report is', 'reports are')} past the 15-day mark from the appointment.</p>
      ) : null}
      <div id="dr-queue-panel" role="tabpanel" aria-label={filters.show === 'today' ? 'Today' : mine ? 'All my reports' : 'All reports'}>
      {phone ? (
        <MatterCardList state={queue.isPending ? 'loading' : 'ready'}>
          {!queue.isPending && visible.length === 0 ? empty : visible.map((row: DoctorRow) => (
            <MatterCard
              key={row.ref}
              href={doctorRowHref(row, onBridge)}
              reference={row.ref}
              patient={row.patientAsOnLoi}
              chip={<StatusChip status={row.status} size="sm" emphasis={row.waitingOn === 'doctor' && mine ? 'mine' : undefined} subState={turnWords(row, mine)} />}
              due={<DueCell dueDate={row.dueDate} />}
              days={<DaysCell days={row.daysWaiting} sla={row.slaState} allowedDays={row.allowedDays} status={row.status} />}
              pairs={[
                { label: 'To decide', value: String(row.workspace?.openSuggestions ?? 0) },
                { label: 'Questions', value: String(row.workspace?.openClarifications ?? 0) },
                { label: 'Empty boxes', value: String(row.workspace?.unanswered ?? 0) },
              ]}
              flags={<FlagMenu matterRef={row.ref} flags={row.flags} max={9} layout="inline" />}
              overdue={row.slaState === 'overdue'}
              action={openLabel(row, onBridge).text}
            />
          ))}
        </MatterCardList>
      ) : (
        <QueueTable
          columns={columns}
          data={visible}
          sorting={sorting}
          onSortingChange={(updater) => { const next = typeof updater === 'function' ? updater(sorting) : updater; const first = next[0]; update(first ? { sort: first.id, dir: first.desc ? 'desc' : 'asc' } : { sort: null }); }}
          getRowId={(r) => r.ref}
          rowHref={(r) => doctorRowHref(r, onBridge)}
          rowState={(r) => (r.slaState === 'overdue' ? 'overdue' : 'default')}
          caption={`${mine ? 'My reports' : `${doctorName}'s reports`}: ${visible.length} ${visible.length === 1 ? 'report' : 'reports'}, due-date order`}
          state={queue.isPending ? 'loading' : 'ready'}
          empty={empty}
        />
      )}
      </div>
      <Legend>
        <LegendItem term={<StatusChip status={6} size="sm" />}>the blank is with the doctor; dictate or type, then Submit draft.</LegendItem>
        <LegendItem term={<StatusChip status={8} size="sm" />}>QA has proposed changes; each waits for Accept or Reject, then Resubmit to QA.</LegendItem>
        <LegendItem term={<StatusChip status={7} size="sm" />}>with QA; read only for the doctor until it returns.</LegendItem>
        <LegendItem term={<span className="font-medium">To decide, Questions, Empty boxes</span>}>QA suggestions undecided; questions from QA unanswered; answer boxes still blank.</LegendItem>
        <LegendItem term={<span className="font-medium">Today</span>}>waiting on the doctor and overdue, due within 48 hours or flagged Urgent.</LegendItem>
      </Legend>
    </div>
  );
}

function DoctorCard({ d }: { d: DoctorCounts }) {
  return (
    <article data-slot="doctor-card" aria-labelledby={`doctor-${d.slug}`} className="flex flex-col gap-2 rounded-xl border border-beam-line bg-beam-paper p-4">
      <span className="flex items-center gap-2"><h2 id={`doctor-${d.slug}`} className="text-sm font-semibold">{d.name}</h2>{d.active ? null : <InactiveMark />}</span>
      <p className="text-[13px] text-beam-text-muted">{d.specialty || 'Specialty not set'}</p>
      <p className="text-[13px] text-beam-text-muted">
        <span className="sr-only">Awaiting draft: </span>{d.counts.drafting} awaiting draft · <span className="sr-only">With QA: </span>{d.counts.withQa} with QA · <span className="sr-only">Deciding: </span>{d.counts.deciding} deciding · <span className={d.counts.overdue > 0 ? 'text-beam-error' : undefined}><span className="sr-only">Overdue: </span>{d.counts.overdue} overdue</span>
      </p>
      <Link to={`/dr?doctor=${encodeURIComponent(d.slug)}`} className={buttonVariants({ size: 'lg', variant: 'outline' }) + ' mt-1 h-11 w-full'} aria-label={`Open ${d.name}'s reports`}>Open</Link>
    </article>
  );
}

function DoctorsList() {
  const counts = useDoctorCounts(true);
  const phone = useIsPhone();
  const online = useOnline();
  const columns = useMemo(() => doctorsColumns(), []);
  const header = <PageHeader title="Doctors" description="Doctor queues and profiles." />;
  if (counts.isError) {
    return (
      <div className="flex flex-col gap-3">
        {header}
        <ErrorPanel message="The doctors could not be loaded." onRetry={() => void counts.refetch()} offline={!online} />
      </div>
    );
  }
  const rows = counts.data ?? [];
  const empty = <EmptyState title="No doctors are on the registry." action={<Link to="/registry/doctors" className={buttonVariants({ variant: 'outline' })}>Add a doctor on the registry</Link>} />;
  return (
    <div className="flex flex-col gap-3">
      {header}
      {phone ? (
        <div data-slot="doctor-card-list" className="flex flex-col gap-3">
          {counts.isPending ? [0, 1, 2].map((i) => <Skeleton key={i} className="h-32 rounded-xl" aria-hidden="true" />) : rows.length === 0 ? empty : rows.map((d) => <DoctorCard key={d.slug} d={d} />)}
        </div>
      ) : (
        <QueueTable
          columns={columns}
          data={rows}
          sorting={[]}
          onSortingChange={() => undefined}
          getRowId={(r) => r.slug}
          rowHref={(r) => `/dr?doctor=${encodeURIComponent(r.slug)}`}
          caption={`Doctors: ${rows.length}, by name`}
          state={counts.isPending ? 'loading' : 'ready'}
          empty={empty}
        />
      )}
    </div>
  );
}
